export type PersistedUploadStatus = "queued" | "uploading" | "success" | "failed" | "cancelled" | "duplicate";

const DB_NAME = "joint-photos-upload-queue";
const DB_VERSION = 1;
const META_STORE = "items";
const BLOB_STORE = "blobs";
const OPFS_DIR = "upload-queue";
const QUOTA_HEADROOM_BYTES = 8 * 1024 * 1024;

export const PERSIST_WARNING =
  "Couldn't save some remaining files on this device. If the app closes, select those again — already uploaded ones will be skipped.";

export type PersistedUploadMeta = {
  id: string;
  ownerId: string;
  albumId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  lastModified: number;
  status: PersistedUploadStatus;
  percent: number;
  error?: string;
  startedAt?: number;
  finishedAt?: number;
  takenAt?: string;
  persisted: boolean;
};

export type RestorableUploadItem = PersistedUploadMeta & {
  file: File;
};

let persistRequested = false;

function isResumableStatus(status: PersistedUploadStatus): boolean {
  return status === "queued" || status === "uploading" || status === "failed";
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        db.createObjectStore(BLOB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Failed to open upload queue database"));
  });
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

export async function requestPersistentStorage(): Promise<void> {
  if (persistRequested) return;
  persistRequested = true;
  try {
    await navigator.storage?.persist?.();
  } catch {
    // Persistence is best-effort; Safari may ignore or delay the prompt.
  }
}

async function hasQuotaFor(size: number): Promise<boolean> {
  if (!navigator.storage?.estimate) return true;
  try {
    const { quota, usage } = await navigator.storage.estimate();
    if (!quota) return true;
    return quota - (usage ?? 0) > size + QUOTA_HEADROOM_BYTES;
  } catch {
    return true;
  }
}

async function writeOpfsBlob(id: string, blob: Blob): Promise<boolean> {
  const storage = navigator.storage;
  if (!storage?.getDirectory) return false;
  try {
    const root = await storage.getDirectory();
    const dir = await root.getDirectoryHandle(OPFS_DIR, { create: true });
    const handle = await dir.getFileHandle(id, { create: true });
    if (typeof handle.createWritable !== "function") return false;
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

async function readOpfsBlob(id: string): Promise<Blob | null> {
  const storage = navigator.storage;
  if (!storage?.getDirectory) return null;
  try {
    const root = await storage.getDirectory();
    const dir = await root.getDirectoryHandle(OPFS_DIR);
    const handle = await dir.getFileHandle(id);
    return await handle.getFile();
  } catch {
    return null;
  }
}

async function deleteOpfsBlob(id: string): Promise<void> {
  const storage = navigator.storage;
  if (!storage?.getDirectory) return;
  try {
    const root = await storage.getDirectory();
    const dir = await root.getDirectoryHandle(OPFS_DIR);
    await dir.removeEntry(id);
  } catch {
    // Missing file is fine.
  }
}

async function writeIdbBlob(id: string, blob: Blob): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(BLOB_STORE, "readwrite");
    tx.objectStore(BLOB_STORE).put(blob, id);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Failed to store upload blob"));
      tx.onabort = () => reject(tx.error ?? new Error("Failed to store upload blob"));
    });
  } finally {
    db.close();
  }
}

async function readIdbBlob(id: string): Promise<Blob | null> {
  const db = await openDb();
  try {
    const tx = db.transaction(BLOB_STORE, "readonly");
    const result = await idbRequest<Blob | undefined>(tx.objectStore(BLOB_STORE).get(id));
    return result ?? null;
  } finally {
    db.close();
  }
}

async function deleteIdbBlob(id: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(BLOB_STORE, "readwrite");
    tx.objectStore(BLOB_STORE).delete(id);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Failed to delete upload blob"));
      tx.onabort = () => reject(tx.error ?? new Error("Failed to delete upload blob"));
    });
  } catch {
    // Ignore cleanup failures.
  } finally {
    db.close();
  }
}

export async function persistUploadBlob(id: string, file: File): Promise<boolean> {
  await requestPersistentStorage();
  if (!(await hasQuotaFor(file.size))) {
    return false;
  }
  if (await writeOpfsBlob(id, file)) {
    return true;
  }
  try {
    await writeIdbBlob(id, file);
    return true;
  } catch {
    return false;
  }
}

export async function readUploadBlob(id: string): Promise<Blob | null> {
  return (await readOpfsBlob(id)) ?? (await readIdbBlob(id));
}

export async function deleteUploadBlob(id: string): Promise<void> {
  await Promise.all([deleteOpfsBlob(id), deleteIdbBlob(id)]);
}

export function toPersistedMeta(item: {
  id: string;
  ownerId: string;
  albumId: string;
  fileName: string;
  fileSize: number;
  file: File;
  status: PersistedUploadStatus;
  percent: number;
  error?: string;
  startedAt?: number;
  finishedAt?: number;
  takenAt?: string;
  persisted?: boolean;
}): PersistedUploadMeta {
  return {
    id: item.id,
    ownerId: item.ownerId,
    albumId: item.albumId,
    fileName: item.fileName,
    fileSize: item.fileSize,
    mimeType: item.file.type || "application/octet-stream",
    lastModified: item.file.lastModified || Date.now(),
    status: item.status,
    percent: item.status === "uploading" ? 0 : item.percent,
    error: item.error,
    startedAt: item.startedAt,
    finishedAt: item.finishedAt,
    takenAt: item.takenAt,
    persisted: item.persisted === true,
  };
}

export async function saveQueueMetadata(items: PersistedUploadMeta[]): Promise<void> {
  const resumable = items.filter((item) => isResumableStatus(item.status));
  const db = await openDb();
  try {
    const tx = db.transaction(META_STORE, "readwrite");
    const store = tx.objectStore(META_STORE);
    store.clear();
    for (const item of resumable) {
      store.put(item);
    }
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Failed to save upload queue"));
      tx.onabort = () => reject(tx.error ?? new Error("Failed to save upload queue"));
    });
  } finally {
    db.close();
  }
}

export async function loadQueueMetadata(): Promise<PersistedUploadMeta[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(META_STORE, "readonly");
    return await idbRequest<PersistedUploadMeta[]>(tx.objectStore(META_STORE).getAll());
  } finally {
    db.close();
  }
}

export async function hydratePersistedQueue(): Promise<{
  items: RestorableUploadItem[];
  persistWarning: string | null;
}> {
  const metas = await loadQueueMetadata();
  const items: RestorableUploadItem[] = [];
  let persistWarning: string | null = null;

  for (const meta of metas) {
    if (!isResumableStatus(meta.status)) continue;

    const blob = meta.persisted ? await readUploadBlob(meta.id) : null;
    const status = meta.status === "uploading" ? "queued" : meta.status;

    if (!blob) {
      persistWarning = PERSIST_WARNING;
      items.push({
        ...meta,
        status: "failed",
        percent: 0,
        startedAt: undefined,
        finishedAt: Date.now(),
        persisted: false,
        error: "This file wasn't saved on this device. Select it again — already uploaded photos will be skipped.",
        file: new File([], meta.fileName, {
          type: meta.mimeType,
          lastModified: meta.lastModified,
        }),
      });
      continue;
    }

    items.push({
      ...meta,
      status,
      percent: status === "queued" ? 0 : meta.percent,
      startedAt: undefined,
      persisted: true,
      file: new File([blob], meta.fileName, {
        type: meta.mimeType,
        lastModified: meta.lastModified,
      }),
    });
  }

  return { items, persistWarning };
}

export async function removePersistedUpload(id: string): Promise<void> {
  await deleteUploadBlob(id);
}
